import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, merge } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TutorialVideoService } from '../tutorial-video.service';

export interface TutorialVideoDialogData {
  videoUrl: string;
}

const TOUCH_CONTROLS_TIMEOUT_MS = 3000;

@Component({
  selector: 'app-tutorial-video-dialog',
  templateUrl: './tutorial-video-dialog.component.html',
  styleUrls: ['./tutorial-video-dialog.component.scss'],
})
export class TutorialVideoDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('videoHost') hostRef!: ElementRef<HTMLDivElement>;
  @ViewChild('videoWrapper') wrapperRef!: ElementRef<HTMLDivElement>;

  readonly pipSupported = !!document.pictureInPictureEnabled;
  isPipActive = false;
  controlsVisible = false;

  private video!: HTMLVideoElement;
  private readonly destroy$ = new Subject<void>();
  private touchHideTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly dialogRef: MatDialogRef<TutorialVideoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: TutorialVideoDialogData,
    private readonly videoService: TutorialVideoService
  ) {
    merge(
      dialogRef.backdropClick(),
      dialogRef.keydownEvents().pipe(filter((e) => e.key === 'Escape'))
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.closeAndSave());
  }

  ngAfterViewInit(): void {
    this.video = this.videoService.getVideoElement(this.data.videoUrl);
    this.videoService.attachToHost(this.hostRef.nativeElement);
    this.videoService.seekToStep();
    this.videoService.startAutoSave();
    this.video.muted = false;
    this.video.play().catch(() => {});
    this.video.addEventListener('enterpictureinpicture', this.onEnterPip);
    this.video.addEventListener('leavepictureinpicture', this.onLeavePip);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.touchHideTimeout);
    this.video?.removeEventListener('enterpictureinpicture', this.onEnterPip);
    this.video?.removeEventListener('leavepictureinpicture', this.onLeavePip);
  }

  /** Called on touchstart to temporarily show controls on mobile. */
  onTouch(): void {
    this.controlsVisible = true;
    clearTimeout(this.touchHideTimeout);
    this.touchHideTimeout = setTimeout(() => {
      this.controlsVisible = false;
    }, TOUCH_CONTROLS_TIMEOUT_MS);
  }

  async togglePip(): Promise<void> {
    if (this.isPipActive) {
      await this.videoService.exitPip();
    } else {
      await this.videoService.enterPip();
    }
  }

  closeAndSave(): void {
    if (!this.video.ended) {
      this.video.pause();
      this.videoService.saveProgress(this.video.currentTime);
    }
    this.videoService.detachFromHost();
    this.videoService.stopAutoSave();
    this.dialogRef.close();
  }

  private readonly onEnterPip = (): void => {
    this.isPipActive = true;
    this.videoService.saveProgress(this.video.currentTime);
    this.videoService.detachFromHost();
    this.videoService.stopAutoSave();
    this.dialogRef.close();
  };

  private readonly onLeavePip = (): void => {
    this.isPipActive = false;
  };
}
